import { CheckboxField, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, useMediaQuery } from '@mui/system'
import React from 'react'
import StickyContentBar from 'src/Libs/header/components/StickyContentBar'
import { useDocumentOptions } from 'src/Routes/Service/Documents/useDocument'
import {
  DocumentLanguageFilterType,
  DocumentSortByFiterType,
  DocumentsCountryFilterType,
  DocumentUserLevelFilterType,
  DownloadsCategoryModel,
} from '../models'
import { makeStyles } from 'tss-react/mui'
import { Button, IconButton, Tooltip, useTheme } from '@mui/material'
import { J2Multiply } from '@e3dc-react/icons'
import { useCategoryDisplayName } from '../hooks'

const useClasses = makeStyles()(theme => ({
  select: {
    minWidth: `calc((100% - ${theme.spacing(2)}) / 2)`,

    [theme.breakpoints.up('md')]: {
      minWidth: 200,
    },
  },
}))

interface FilterBarProps {
  filterString?: string
  setFilterString: (value: string) => void
  language: DocumentLanguageFilterType
  setLanguage: (value: DocumentLanguageFilterType) => void
  userLevel?: DocumentUserLevelFilterType
  setUserLevel: (value: DocumentUserLevelFilterType) => void
  country?: DocumentsCountryFilterType
  setCountry: (value: DocumentsCountryFilterType) => void
  isServiceUser?: boolean
  searchInCategory?: boolean
  setSearchInCategory: (value: boolean) => void
  category?: DownloadsCategoryModel
  clearFilters: () => void
  sortBy?: DocumentSortByFiterType
  setSortBy: (value: DocumentSortByFiterType) => void
}

/**
 * Component that holds the filters for the documents list.
 * Sticks to the header when scrolling.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  filterString,
  setFilterString,
  userLevel,
  setUserLevel,
  language,
  setLanguage,
  country,
  setCountry,
  isServiceUser,
  searchInCategory,
  setSearchInCategory,
  sortBy,
  setSortBy,
  category,
  clearFilters,
}) => {
  const { languageOptions, userLevelOptions, countryOptions, sortOptions } = useDocumentOptions(true)
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { classes } = useClasses()
  const getCategoryDisplayName = useCategoryDisplayName()

  return (
    <StickyContentBar sx={{ marginBottom: 2 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="center" justifyContent={'space-between'}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <TextField
            placeholder={t('common.search')}
            value={filterString}
            onChange={(value: string) => {
              setFilterString(value)
            }}
            data-qa={'input-documents-search'}
            fullWidth={isXs}
            sx={{ minWidth: 200, flexGrow: 1 }}
          />
          {category && category.path !== 'Root' && (
            <CheckboxField
              label={t('downloads.filter.search-in-category', { categoryName: getCategoryDisplayName(category.nameLanguages) })}
              value={searchInCategory}
              onChange={(value: boolean) => {
                setSearchInCategory(value)
              }}
              sx={{ ml: 2 }}
            />
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <Stack
            sx={{ order: { xs: 3, sm: 0 }, mt: { xs: 2, sm: 0 }, mb: { xs: 0 }, mr: { xs: 0, sm: 2 } }}
            direction={{ xs: 'row', sm: 'row' }}
            spacing={2}
            alignItems="center"
            width={'100%'}
          >
            {isServiceUser && (
              <Select
                options={userLevelOptions}
                value={userLevel}
                onChange={setUserLevel}
                label={t('documents.filter.user-level.label')}
                dataQa={'select-documents-user-level'}
                fullWidth
                className={classes.select}
              />
            )}
            {downSm && (
              <Select
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                label={t('documents.filter.sort-by.label')}
                dataQa={'select-documents-sort-by'}
                fullWidth
                className={classes.select}
              />
            )}
          </Stack>
          <Stack direction={{ xs: 'row', sm: 'row' }} spacing={2} alignItems="center" width={'100%'}>
            <Select
              options={languageOptions}
              value={language}
              onChange={setLanguage}
              label={t('common.language')}
              dataQa={'select-documents-language'}
              className={classes.select}
              fullWidth={!isXs}
              sx={{ width: '100%' }}
            />
            <Select
              options={countryOptions}
              value={country}
              onChange={setCountry}
              label={t('common.country')}
              dataQa={'select-documents-country'}
              fullWidth={!isXs}
              className={classes.select}
              sx={{ width: '100%' }}
            />
            {!downSm && (
              <Select
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                label={t('documents.filter.sort-by.label')}
                dataQa={'select-documents-sort-by'}
                fullWidth={!isXs}
                className={classes.select}
                sx={{ order: { xs: 3, sm: 0 }, mt: { xs: 2, sm: 0 }, mb: { xs: 0 }, mr: { xs: 0, sm: 2 } }}
              />
            )}
          </Stack>
          {isXs ? (
            <Button fullWidth variant="text" onClick={clearFilters} sx={{ mt: 2, order: 4 }} data-qa={'button-clear-filters'}>
              {t('documents.filter.clear-filters')}
            </Button>
          ) : (
            <Tooltip title={t('documents.filter.clear-filters')}>
              <IconButton onClick={clearFilters} size="large" sx={{ mr: -2 }} data-qa={'button-clear-filters'}>
                <J2Multiply />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </StickyContentBar>
  )
}

export default FilterBar
